import type { INavItem } from "src/components/layouts";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { hasPermission, hasPermissionAnyMatch } from "src/helpers/authentication";
import { helpRouteCatalog, helpRoutePermission } from "src/helpers/helpRouteCatalog";
import * as Dtos from "src/dtos/Tmd.dtos";
import type {
    AppRouteHandle,
    AppRouteObject,
    RouteMetadataContext,
    RouteMetadataValue,
} from "./types";

export interface RouteMatchLike {
    pathname: string;
    params: Readonly<Record<string, string | undefined>>;
    /** React Router's UIMatch exposes the decorated handle directly. */
    handle?: AppRouteHandle;
    /** Retained only for route-tree unit tests and compatibility callers. */
    route?: AppRouteObject;
}

export interface ResolvedRouteMetadata {
    title: string;
    helpTopicKey: string | null;
    breadcrumbs: INavItem[];
    drawerItems: INavItem[];
    access: AppRouteHandle["access"];
    /** Params from the leaf match, passed to scoped permission checks. */
    params: Readonly<Record<string, string | undefined>>;
}

const primaryNavigationDefaults: Record<string, { navigation: NonNullable<AppRouteHandle["navigation"]>; access?: AppRouteHandle["access"] }> = {
    dashboard: { navigation: { title: "Dashboard", icon: FontAwesomeIcons.Duotone.HOME, primary: true, order: 0 } },
    trials: { navigation: { title: "Trials", icon: FontAwesomeIcons.Duotone.TRIAL, primary: true, order: 1 }, access: { permission: Dtos.Permission.TrialView } },
    institutions: { navigation: { title: "Institutions", icon: FontAwesomeIcons.Duotone.INSTITUTION, primary: true, order: 2 }, access: { permission: Dtos.Permission.InstitutionView } },
    groups: { navigation: { title: "Groups", icon: FontAwesomeIcons.Duotone.GROUP, primary: true, order: 3 }, access: { permission: Dtos.Permission.GroupView } },
    labs: { navigation: { title: "Labs", icon: FontAwesomeIcons.Duotone.FLASKVIAL, primary: true, order: 4 }, access: { permission: Dtos.Permission.LabView } },
    projects: { navigation: { title: "Projects", icon: FontAwesomeIcons.Duotone.CLIPBOARD, primary: true, order: 5 }, access: { permission: Dtos.Permission.ProjectView } },
    personnel: { navigation: { title: "Personnel", icon: FontAwesomeIcons.Duotone.USER, primary: true, order: 6 }, access: { permission: Dtos.Permission.PersonnelView } },
    committees: { navigation: { title: "Committee", icon: FontAwesomeIcons.Duotone.COMMITTEE, primary: true, order: 7 }, access: { permission: Dtos.Permission.CommitteeView } },
    settings: { navigation: { title: "Settings", icon: FontAwesomeIcons.Duotone.SETTINGS, primary: true, order: 8 }, access: { permission: Dtos.Permission.SettingsView } },
    search: { navigation: { title: "Search", icon: FontAwesomeIcons.Duotone.SEARCH, primary: true, order: 9 } },
};

// Settings cards and drawer entries are route metadata, rather than a second
// menu definition.  The path keys keep the legacy wording, icons and order
// next to the canonical destinations that consume them.
const settingsNavigationDefaults: Record<string, NonNullable<AppRouteHandle["navigation"]>> = {
    "/settings/general": { title: "General", description: "Manage general system settings, including roles and professional disciplines.", icon: FontAwesomeIcons.Duotone.COG, drawer: true, order: 0 },
    "/settings/project": { title: "Project", description: "Configure project related templates and settings.", icon: FontAwesomeIcons.Duotone.CLIPBOARD, drawer: true, order: 1 },
    "/settings/trial": { title: "Trial", description: "Manage trial related templates and settings.", icon: FontAwesomeIcons.Duotone.TRIAL, drawer: true, order: 2 },
    "/settings/trialinstitution": { title: "Trial Institution", description: "Manage trial institution related templates and settings.", icon: FontAwesomeIcons.Duotone.TRIALINSTITUTION, drawer: true, order: 3 },
    "/settings/trialgroup": { title: "Trial Group", description: "Manage trial group related templates and settings.", icon: FontAwesomeIcons.Duotone.TRIALGROUP, drawer: true, order: 4 },
    "/settings/group": { title: "Group", description: "Set up and manage research or study groups.", icon: FontAwesomeIcons.Duotone.GROUP, drawer: true, order: 5 },
    "/settings/institution": { title: "Institution", description: "Manage site locations, key contacts, and institution types.", icon: FontAwesomeIcons.Duotone.INSTITUTION, drawer: true, order: 6 },
    "/settings/hrecs": { title: "HREC Manager", description: "Manage Human Research Ethics Committee (HREC) settings.", icon: FontAwesomeIcons.Duotone.HANDHOLDINGHEART, drawer: true, order: 7 },
    "/settings/healthdistricts": { title: "Health District Manager", description: "Oversee health district configurations and related settings.", icon: FontAwesomeIcons.Duotone.HOSPITALS, drawer: true, order: 8 },
    "/settings/general/roles": { title: "Roles", description: "Define and manage user roles and permissions.", icon: FontAwesomeIcons.Duotone.USERSHIELD, drawer: true, order: 0 },
    "/settings/general/countries": { title: "Countries", description: "Manage country information used within the system.", icon: FontAwesomeIcons.Duotone.EARTHOCEANIA, drawer: true, order: 1 },
    "/settings/general/states": { title: "States", description: "Manage states or provinces related to countries.", icon: FontAwesomeIcons.Duotone.ROUTEINTERSTATE, drawer: true, order: 2 },
    "/settings/general/itemcomponenttypes": { title: "Item Component Types", description: "Define types of components used in system items.", icon: FontAwesomeIcons.Duotone.LISTTIMELINE, drawer: true, order: 3 },
    "/settings/general/notetypes": { title: "Note Types", description: "Manage different types of notes used in the system.", icon: FontAwesomeIcons.Duotone.LISTRADIO, drawer: true, order: 4 },
    "/settings/general/interests": { title: "Interests", description: "Manage user interests and areas of focus.", icon: FontAwesomeIcons.Duotone.STOMACH, drawer: true, order: 5 },
    "/settings/general/membershiptypes": { title: "Membership Types", description: "Define and manage different types of memberships.", icon: FontAwesomeIcons.Duotone.PEOPLEGROUP, drawer: true, order: 6 },
    "/settings/general/professionaldisciplines": { title: "Professional Disciplines", description: "Manage and categorize professional disciplines.", icon: FontAwesomeIcons.Duotone.USERDOCTOR, drawer: true, order: 7 },
    "/settings/general/salutations": { title: "Salutations", description: "Manage available salutations for users.", icon: FontAwesomeIcons.Duotone.PERSONHALFDRESS, drawer: true, order: 8 },
    "/settings/project/itemtemplates": { title: "Item Templates", description: "Manage predefined templates for project items.", icon: FontAwesomeIcons.Duotone.FOLDERS, drawer: true, order: 0 },
    "/settings/project/itemcomponents": { title: "Item Components", description: "Define and manage components that make up project items.", icon: FontAwesomeIcons.Duotone.SOLARSYSTEM, drawer: true, order: 1 },
    "/settings/project/itemCategories": { title: "Item Categories", description: "Categorise project items for better organization and filtering.", icon: FontAwesomeIcons.Duotone.SHAPES, drawer: true, order: 2 },
    "/settings/trial/itemtemplates": { title: "Item Templates", description: "Manage predefined templates for trial items.", icon: FontAwesomeIcons.Duotone.FOLDERS, drawer: true, order: 0 },
    "/settings/trial/itemcomponents": { title: "Item Components", description: "Define and manage components that make up trial items.", icon: FontAwesomeIcons.Duotone.SOLARSYSTEM, drawer: true, order: 1 },
    "/settings/trial/itemtemplatecategories": { title: "Item Categories", description: "Organize trial items into different categories.", icon: FontAwesomeIcons.Duotone.SHAPES, drawer: true, order: 2 },
    "/settings/trial/trialtypes": { title: "Trial Types", description: "Define and categorize different types of clinical trials.", icon: FontAwesomeIcons.Duotone.ATOMSIMPLE, drawer: true, order: 3 },
    "/settings/trialinstitution/itemtemplates": { title: "Items Templates", description: "Manage predefined templates for trial institution items.", icon: FontAwesomeIcons.Duotone.FOLDERS, drawer: true, order: 0 },
    "/settings/trialinstitution/itemcomponents": { title: "Items Components", description: "Define and manage components that make up trial institution items.", icon: FontAwesomeIcons.Duotone.SOLARSYSTEM, drawer: true, order: 1 },
    "/settings/trialinstitution/itemtemplatecategories": { title: "Item Categories", description: "Organize trial institution items into different categories.", icon: FontAwesomeIcons.Duotone.SHAPES, drawer: true, order: 2 },
    "/settings/trialgroup/itemtemplates": { title: "Items Templates", description: "Manage predefined templates for trial group items.", icon: FontAwesomeIcons.Duotone.FOLDERS, drawer: true, order: 0 },
    "/settings/trialgroup/itemcomponents": { title: "Items Components", description: "Define and manage components that make up trial group items.", icon: FontAwesomeIcons.Duotone.SOLARSYSTEM, drawer: true, order: 1 },
    "/settings/trialgroup/itemtemplatecategories": { title: "Item Categories", description: "Organize trial group items into different categories.", icon: FontAwesomeIcons.Duotone.SHAPES, drawer: true, order: 2 },
    "/settings/group/itemtemplates": { title: "Item Templates", description: "Manage predefined templates for group items.", icon: FontAwesomeIcons.Duotone.FOLDERS, drawer: true, order: 0 },
    "/settings/group/itemcomponents": { title: "Item Components", description: "Define and manage components that make up group items.", icon: FontAwesomeIcons.Duotone.SOLARSYSTEM, drawer: true, order: 1 },
    "/settings/group/itemCategories": { title: "Item Categories", description: "Categorise group items for better organization and filtering.", icon: FontAwesomeIcons.Duotone.SHAPES, drawer: true, order: 2 },
    "/settings/institution/itemtemplates": { title: "Items Templates", description: "Manage predefined templates for institution items.", icon: FontAwesomeIcons.Duotone.FOLDERS, drawer: true, order: 0 },
    "/settings/institution/itemcomponents": { title: "Items Components", description: "Define and manage components that make up institution items.", icon: FontAwesomeIcons.Duotone.SOLARSYSTEM, drawer: true, order: 1 },
    "/settings/institution/itemtemplatecategories": { title: "Item Categories", description: "Categorise institution items for better organization and filtering.", icon: FontAwesomeIcons.Duotone.SHAPES, drawer: true, order: 2 },
    "/settings/institution/sitelocations": { title: "Site Locations", description: "Manage and configure institution site locations.", icon: FontAwesomeIcons.Duotone.BUILDINGFLAG, drawer: true, order: 3 },
    "/settings/institution/keycontacts": { title: "Key Contacts", description: "Maintain key contacts associated with institutions.", icon: FontAwesomeIcons.Duotone.ADDRESSBOOK, drawer: true, order: 4 },
    "/settings/institution/institutiontypes": { title: "Institution Types", description: "Define and categorize different types of institutions.", icon: FontAwesomeIcons.Duotone.HOUSEMEDICAL, drawer: true, order: 5 },
    "/settings/institution/institutioncodings": { title: "Institution Coding", description: "Configure coding systems for institutions.", icon: FontAwesomeIcons.Duotone.CODE, drawer: true, order: 6 },
};

const legacyDrawerNavigationDefaults: Record<string, NonNullable<AppRouteHandle["navigation"]>> = {
    "/trials/:trialId/details": { title: "Details", icon: FontAwesomeIcons.Duotone.CIRCLEINFO, drawer: true, order: 0 },
    "/trials/:trialId/institutions": { title: "Institutions", icon: FontAwesomeIcons.Duotone.INSTITUTION, drawer: true, order: 1 },
    "/trials/:trialId/trialitems": { title: "Items", icon: FontAwesomeIcons.Duotone.I, drawer: true, order: 2 },
    "/trials/:trialId/trialinstitutionitemviews": { title: "Institution Item Views", icon: FontAwesomeIcons.Duotone.PENFIELD, drawer: true, order: 3 },
    "/trials/:trialId/personnel": { title: "Personnel", icon: FontAwesomeIcons.Duotone.USER, drawer: true, order: 4 },
    "/trials/:trialId/accrual": { title: "Accrual", icon: FontAwesomeIcons.Duotone.CHARTLINE, drawer: true, order: 5 },
    "/trials/:trialId/notes": { title: "Notes", icon: FontAwesomeIcons.Duotone.NOTESMEDICAL, drawer: true, order: 6 },
    "/trials/:trialId/groups": { title: "Groups", icon: FontAwesomeIcons.Duotone.GROUP, drawer: true, order: 7 },
    "/trials/:trialId/datasharing": { title: "Data Sharing", icon: FontAwesomeIcons.Duotone.RIGHTLEFTLARGE, drawer: true, order: 8 },
    "/trials/:trialId/website": { title: "Website", icon: FontAwesomeIcons.Duotone.GLOBEPOINTER, drawer: true, order: 9 },
    "/trials/:trialId/finance": { title: "Finance", icon: FontAwesomeIcons.Duotone.MONEYCHECKDOLLARPEN, drawer: true, order: 10 },
    "/trials/:trialId/labs": { title: "Labs", icon: FontAwesomeIcons.Duotone.FLASKVIAL, drawer: true, order: 12 },
    "/trials/:trialId/trialinstitution/:trialInstitutionId/details": { title: "Details", icon: FontAwesomeIcons.Duotone.CIRCLEINFO, drawer: true, order: 0 },
    "/trials/:trialId/trialinstitution/:trialInstitutionId/items": { title: "Items", icon: FontAwesomeIcons.Duotone.I, drawer: true, order: 1 },
    "/trials/:trialId/trialinstitution/:trialInstitutionId/personnel": { title: "Personnel", icon: FontAwesomeIcons.Duotone.USER, drawer: true, order: 2 },
    "/trials/:trialId/trialinstitution/:trialInstitutionId/notes": { title: "Notes", icon: FontAwesomeIcons.Duotone.NOTESMEDICAL, drawer: true, order: 3 },
    "/trials/:trialId/trialinstitution/:trialInstitutionId/labs": { title: "Labs", icon: FontAwesomeIcons.Duotone.FLASKVIAL, drawer: true, order: 4 },
    "/trials/:trialId/trialinstitution/:trialInstitutionId/finance": { title: "Finance", icon: FontAwesomeIcons.Duotone.MONEYCHECKDOLLARPEN, drawer: true, order: 5 },
    "/trials/:trialId/trialinstitution/:trialInstitutionId/contracts": { title: "Contracts", icon: FontAwesomeIcons.Duotone.FILECONTRACT, drawer: true, order: 6 },
    "/trials/:trialId/trialinstitution/:trialInstitutionId/monitoring": { title: "Monitoring", icon: FontAwesomeIcons.Duotone.MONITOR, drawer: true, order: 7 },
    "/trials/:trialId/groups/:groupId/trialgroup/:trialGroupId/details": { title: "Details", icon: FontAwesomeIcons.Duotone.CIRCLEINFO, drawer: true, order: 0 },
    "/trials/:trialId/groups/:groupId/trialgroup/:trialGroupId/personnel": { title: "Personnel", icon: FontAwesomeIcons.Duotone.USER, drawer: true, order: 1 },
    "/trials/:trialId/groups/:groupId/trialgroup/:trialGroupId/items": { title: "Items", icon: FontAwesomeIcons.Duotone.I, drawer: true, order: 2 },
    "/trials/:trialId/groups/:groupId/trialgroup/:trialGroupId/labs": { title: "Labs", icon: FontAwesomeIcons.Duotone.FLASKVIAL, drawer: true, order: 3 },
    "/trials/:trialId/groups/:groupId/trialgroup/:trialGroupId/finance": { title: "Finance", icon: FontAwesomeIcons.Duotone.MONEYCHECKDOLLARPEN, drawer: true, order: 4 },
    "/trials/:trialId/groups/:groupId/trialgroup/:trialGroupId/contracts": { title: "Contract", icon: FontAwesomeIcons.Duotone.FILECONTRACT, drawer: true, order: 5 },
};

const addLegacyDrawerEntries = (base: string, entries: readonly [string, string, NonNullable<AppRouteHandle["navigation"]>["icon"]][]): void => {
    entries.forEach(([path, title, icon], order) => {
        legacyDrawerNavigationDefaults[`${base}/${path}`] = { title, icon, drawer: true, order };
    });
};

addLegacyDrawerEntries("/groups/:groupId", [
    ["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO], ["trials", "Trials", FontAwesomeIcons.Duotone.TRIAL],
    ["personnel", "Personnel", FontAwesomeIcons.Duotone.USER], ["items", "Items", FontAwesomeIcons.Duotone.I],
    ["notes", "Notes", FontAwesomeIcons.Duotone.NOTESMEDICAL], ["finances", "Finance", FontAwesomeIcons.Duotone.MONEYCHECKDOLLARPEN],
    ["contracts", "Contracts", FontAwesomeIcons.Duotone.FILECONTRACT],
]);
addLegacyDrawerEntries("/groups/:groupId/trials/:trialId/trialgroup/:trialGroupId", [
    ["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO], ["personnel", "Personnel", FontAwesomeIcons.Duotone.USER],
    ["items", "Items", FontAwesomeIcons.Duotone.I], ["labs", "Labs", FontAwesomeIcons.Duotone.FLASKVIAL],
    ["finance", "Finance", FontAwesomeIcons.Duotone.MONEYCHECKDOLLARPEN], ["contracts", "Contracts", FontAwesomeIcons.Duotone.FILECONTRACT],
]);
addLegacyDrawerEntries("/committees/:committeeId", [
    ["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO], ["meetings", "Meetings", FontAwesomeIcons.Duotone.HANDSHAKE],
    ["members", "Members", FontAwesomeIcons.Duotone.USER], ["regularinvitees", "Regular Invitees", FontAwesomeIcons.Duotone.CALENDARCIRCLEUSER],
]);
addLegacyDrawerEntries("/committees/:committeeId/meetings/:committeeMeetingId", [
    ["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO], ["attendances", "Attendance", FontAwesomeIcons.Duotone.CLIPBOARDUSER],
]);
addLegacyDrawerEntries("/committees/:committeeId/meetings/:committeeMeetingId/attendances/:committeeMeetingAttendanceId", [["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO]]);
addLegacyDrawerEntries("/committees/:committeeId/members/:committeeMemberId", [
    ["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO], ["roles", "Roles", FontAwesomeIcons.Duotone.USERSHIELD],
]);
addLegacyDrawerEntries("/committees/:committeeId/members/:committeeMemberId/roles/:committeeMemberRoleId", [["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO]]);
addLegacyDrawerEntries("/committees/:committeeId/regularinvitees/:committeeRegularInviteeId", [["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO]]);
addLegacyDrawerEntries("/institutions/:institutionId", [
    ["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO], ["institutionitems", "Items", FontAwesomeIcons.Duotone.I],
    ["personnel", "Personnel", FontAwesomeIcons.Duotone.USER], ["labs", "Labs", FontAwesomeIcons.Duotone.FLASKVIAL],
    ["notes", "Notes", FontAwesomeIcons.Duotone.NOTESMEDICAL], ["procedures", "Procedures", FontAwesomeIcons.Duotone.BOOK],
    ["finance", "Finance", FontAwesomeIcons.Duotone.MONEYCHECKDOLLARPEN], ["contract", "Contract", FontAwesomeIcons.Duotone.FILECONTRACT],
    ["codes", "Codes", FontAwesomeIcons.Duotone.SQUARECODE], ["trials", "Trials", FontAwesomeIcons.Duotone.TRIAL],
]);
addLegacyDrawerEntries("/labs/:laboratoryId", [
    ["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO], ["departments", "Departments", FontAwesomeIcons.Duotone.BUILDINGMEMO],
    ["personnel", "Personnel", FontAwesomeIcons.Duotone.USER], ["institutions", "Institutions", FontAwesomeIcons.Duotone.INSTITUTION],
    ["finance", "Finance", FontAwesomeIcons.Duotone.MONEYCHECKDOLLARPEN], ["contract", "Contract", FontAwesomeIcons.Duotone.FILECONTRACT],
]);
addLegacyDrawerEntries("/personnel/:personnelId", [
    ["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO], ["trainingcertifications", "Training & Certifications", FontAwesomeIcons.Duotone.FILECERTIFICATE],
    ["roles", "Roles", FontAwesomeIcons.Duotone.USERSHIELD], ["permissions", "Permissions", FontAwesomeIcons.Duotone.USERSHIELD],
    ["notes", "Notes", FontAwesomeIcons.Duotone.NOTESMEDICAL], ["activedirectory", "Login & Security", FontAwesomeIcons.Duotone.SHIELDHALVED],
    ["fellowship", "Fellowships", FontAwesomeIcons.Duotone.BULIDINGCOLUMNS],
]);
addLegacyDrawerEntries("/projects/:projectId", [
    ["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO], ["items", "Items", FontAwesomeIcons.Duotone.I],
    ["discussions", "Discussions", FontAwesomeIcons.Duotone.COMMENTS],
]);
addLegacyDrawerEntries("/trials/:projectId/project", [
    ["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO], ["discussions", "Discussions", FontAwesomeIcons.Duotone.COMMENTS],
    ["items", "Items", FontAwesomeIcons.Duotone.I],
]);
addLegacyDrawerEntries("/projects/:projectId/discussions/:discussionId", [["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO]]);
addLegacyDrawerEntries("/trials/:projectId/project/discussions/:discussionId", [["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO]]);
addLegacyDrawerEntries("/institutions/:institutionId/codes/:institutionCodeId", [["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO]]);
addLegacyDrawerEntries("/institutions/:institutionId/notes/:noteId", [["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO]]);
addLegacyDrawerEntries("/personnel/:personnelId/roles/:personnelRoleId", [["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO]]);
addLegacyDrawerEntries("/personnel/:personnelId/notes/:noteId", [["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO]]);
addLegacyDrawerEntries("/personnel/:personnelId/fellowship/:fellowshipId", [["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO]]);

// Record drawers that were wired before the route metadata conversion.
const detailRecordDrawers = [
    "/groups/:groupId/personnel/:trialPersonnelId",
    "/groups/:groupId/notes/:noteId",
    "/groups/:groupId/trials/:trialId/trialgroup/:trialGroupId/personnel/:trialPersonnelId",
    "/groups/:groupId/trials/:trialId/trialgroup/:trialGroupId/contracts/:trialGroupContractId",
    "/groups/:groupId/trials/:trialId/trialgroup/:trialGroupId/labs/:trialGroupLaboratoryId/contracts/:trialGroupLaboratoryContractId",
    "/groups/:groupId/trials/:trialId/trialgroup/:trialGroupId/labs/:trialGroupLaboratoryId/personnel/:trialGroupLaboratoryPersonnelId",
    "/trials/:trialId/groups/:groupId/trialgroup/:trialGroupId/personnel/:trialPersonnelId",
    "/trials/:trialId/groups/:groupId/trialgroup/:trialGroupId/contracts/:trialGroupContractId",
    "/trials/:trialId/groups/:groupId/trialgroup/:trialGroupId/labs/:trialGroupLaboratoryId/contracts/:trialGroupLaboratoryContractId",
    "/trials/:trialId/groups/:groupId/trialgroup/:trialGroupId/labs/:trialGroupLaboratoryId/personnel/:trialGroupLaboratoryPersonnelId",
    "/trials/:trialId/notes/:noteId",
    "/settings/hrecs/:hrecId",
    "/settings/healthdistricts/:healthDistrictId",
    "/trials/:trialId/trialinstitution/:trialInstitutionId/notes/:noteId",
    "/trials/:trialId/trialinstitution/:trialInstitutionId/personnel/:personnelId",
    "/trials/:trialId/trialinstitution/:trialInstitutionId/labs/:trialInstitutionLaboratoryId/personnel/:trialInstitutionLaboratoryPersonnelId",
    "/trials/:trialId/trialinstitution/:trialInstitutionId/contracts/:trialInstitutionContractId",
    "/trials/:trialId/trialinstitution/:trialInstitutionId/labs/:trialInstitutionLaboratoryId/contracts/:trialInstitutionLaboratoryContractId",
    "/labs/:laboratoryId/departments/:laboratoryDepartmentId",
    "/labs/:laboratoryId/personnel/:laboratoryPersonnelId",
    "/labs/:laboratoryId/institutions/:institutionLaboratoryId",
    "/institutions/:institutionId/labs/:institutionLaboratoryId",
    "/institutions/:institutionId/personnel/:trialPersonnelId",
    "/trials/:trialId/personnel/:trialPersonnelId",
    "/institutions/:institutionId/procedures/:institutionProcedureId",
    "/trials/:trialId/trialinstitution/:trialInstitutionId/monitoring/:monitoringId/documents/:monitoringDocumentId",
    "/trials/:trialId/trialinstitution/:trialInstitutionId/monitoring/:monitoringId/issues/:monitoringIssueId/documents/:monitoringIssueDocumentId",
] as const;
detailRecordDrawers.forEach(base => addLegacyDrawerEntries(base, [["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO]]));

const trialLaboratoryDrawers = [
    "/trials/:trialId/trialinstitution/:trialInstitutionId/labs/:trialInstitutionLaboratoryId",
    "/trials/:trialId/groups/:groupId/trialgroup/:trialGroupId/labs/:trialGroupLaboratoryId",
    "/groups/:groupId/trials/:trialId/trialgroup/:trialGroupId/labs/:trialGroupLaboratoryId",
] as const;
trialLaboratoryDrawers.forEach(base => addLegacyDrawerEntries(base, [
    ["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO],
    ["personnel", "Personnel", FontAwesomeIcons.Duotone.USER],
    ["finance", "Finance", FontAwesomeIcons.Duotone.MONEYCHECKDOLLARPEN],
    ["contracts", "Contracts", FontAwesomeIcons.Duotone.FILECONTRACT],
]));
addLegacyDrawerEntries("/trials/:trialId/trialinstitution/:trialInstitutionId/monitoring/:monitoringId", [
    ["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO],
    ["documents", "Documents", FontAwesomeIcons.Duotone.FILE],
    ["issues", "Issues", FontAwesomeIcons.Duotone.MONITORINGISSUE],
]);
addLegacyDrawerEntries("/trials/:trialId/trialinstitution/:trialInstitutionId/monitoring/:monitoringId/issues/:monitoringIssueId", [
    ["details", "Details", FontAwesomeIcons.Duotone.CIRCLEINFO],
    ["documents", "Documents", FontAwesomeIcons.Duotone.FILE],
]);

export const evaluateRouteValue = <T,>(value: RouteMetadataValue<T> | undefined, context: RouteMetadataContext, fallback: T): T => {
    if (value === undefined) return fallback;
    return typeof value === "function" ? (value as (context: RouteMetadataContext) => T)(context) : value;
};

const humanizeSegment = (segment: string): string => {
    const normalized = segment.replace(/^:/, "").replace(/[-_]/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");
    return normalized.replace(/\b\w/g, character => character.toUpperCase());
};

const legacyRouteLabels: Record<string, string> = {
    trialitems: "Items",
    institutionitems: "Items",
    trialinstitutionitemviews: "Custom Views",
    trainingcertifications: "Training & Certifications",
    activedirectory: "Login & Security",
    datasharing: "Data Sharing",
    finances: "Finance",
    monitoring: "Monitoring",
    documents: "Documents",
    issues: "Issues",
};

const defaultRouteIcon = (path?: string): typeof FontAwesomeIcons.Duotone.HOME | undefined => {
    const segment = path?.split("/").filter(Boolean).at(-1);
    switch (segment) {
        case "items":
        case "trialitems":
        case "institutionitems": return FontAwesomeIcons.Duotone.I;
        case "personnel": return FontAwesomeIcons.Duotone.USER;
        case "notes": return FontAwesomeIcons.Duotone.NOTESMEDICAL;
        case "labs": return FontAwesomeIcons.Duotone.FLASKVIAL;
        case "finance":
        case "finances": return FontAwesomeIcons.Duotone.MONEYCHECKDOLLARPEN;
        case "contract":
        case "contracts": return FontAwesomeIcons.Duotone.FILECONTRACT;
        case "accrual": return FontAwesomeIcons.Duotone.CHARTLINE;
        case "monitoring": return FontAwesomeIcons.Duotone.MONITOR;
        case "documents": return FontAwesomeIcons.Duotone.FILE;
        case "issues": return FontAwesomeIcons.Duotone.EXCLAMATION;
        case "website":
        case "datasharing": return FontAwesomeIcons.Duotone.GLOBE;
        case "trainingcertifications": return FontAwesomeIcons.Duotone.FILECERTIFICATE;
        case "activedirectory": return FontAwesomeIcons.Duotone.SHIELDHALVED;
        default: return undefined;
    }
};

const dynamicTopicIcon = (topic?: string): typeof FontAwesomeIcons.Duotone.HOME | undefined => {
    switch (topic) {
        case "country": return FontAwesomeIcons.Duotone.FLAG;
        case "role": return FontAwesomeIcons.Duotone.USERSHIELD;
        case "state": return FontAwesomeIcons.Duotone.ROUTEHIGHWAY;
        case "interest": return FontAwesomeIcons.Duotone.KIDENEYS;
        case "membershiptype": return FontAwesomeIcons.Duotone.IDBADGE;
        case "professional-discipline": return FontAwesomeIcons.Duotone.STETHOSCOPE;
        case "salutation": return FontAwesomeIcons.Duotone.TAGS;
        case "item-component-type": return FontAwesomeIcons.Duotone.OCTAGON;
        case "site-location": return FontAwesomeIcons.Duotone.BUILDINGFLAG;
        case "key-contact": return FontAwesomeIcons.Duotone.ADDRESSBOOK;
        case "institution-type": return FontAwesomeIcons.Duotone.HOUSEMEDICAL;
        case "institution-coding": return FontAwesomeIcons.Duotone.CODE;
        default: return undefined;
    }
};

const dynamicTopicLabels: Record<string, readonly [string, string]> = {
    country: ["countryName", "Country"],
    role: ["roleName", "Role"],
    state: ["stateName", "State"],
    interest: ["interestName", "Interest"],
    membershiptype: ["membershipTypeName", "Membership Type"],
    "professional-discipline": ["professionalDisciplineName", "Professional Discipline"],
    salutation: ["salutationName", "Salutation"],
    "item-component-type": ["itemComponentTypeName", "Item Component Type"],
    "trial-type": ["trialTypeName", "Trial Type"],
    "site-location": ["siteLocationName", "Site Location"],
    "key-contact": ["keyContactName", "Key Contact"],
    "institution-type": ["institutionTypeName", "Institution Type"],
    "institution-coding": ["institutionCodeName", "Institution Coding"],
    hrec: ["hrecName", "HREC"],
    "health-district": ["healthDistrictName", "Health District"],
};

const dynamicRouteLabel = (topic: string | undefined, path: string, context: RouteMetadataContext): string => {
    const parameterLabels: Array<[string, string, string]> = [
        [":monitoringDocumentId", "monitoringDocumentName", "Document"],
        [":monitoringIssueDocumentId", "monitoringIssueDocumentName", "Issue Document"],
        [":monitoringIssueId", "monitoringIssueName", "Issue"],
        [":monitoringId", "monitoringName", "Monitoring"],
        [":trialInstitutionLaboratoryContractId", "contractName", "Contract"],
        [":trialGroupLaboratoryContractId", "contractName", "Contract"],
        [":trialGroupContractId", "contractName", "Contract"],
        [":trialItemTemplateId", "templateName", "Template"],
        [":groupItemTemplateId", "templateName", "Template"],
        [":institutionItemTemplateId", "templateName", "Template"],
        [":trialGroupItemTemplateId", "templateName", "Template"],
        [":trialInstitutionItemTemplateId", "templateName", "Template"],
        [":projectItemTemplateId", "templateName", "Template"],
        [":trialInstitutionLaboratoryPersonnelId", "personnelName", "Personnel"],
        [":trialGroupLaboratoryPersonnelId", "personnelName", "Personnel"],
        [":trialInstitutionLaboratoryId", "laboratoryName", "Laboratory"],
        [":trialGroupLaboratoryId", "laboratoryName", "Laboratory"],
        [":trialInstitutionItemCustomViewId", "customViewName", "Custom View"],
        [":projectInvestigatorId", "investigatorName", "Investigator"],
        [":discussionId", "discussionName", "Discussion"],
        [":trialPersonnelId", "personnelName", "Personnel"],
        [":personnelId", "personnelName", "Personnel"],
        [":noteId", "noteName", "Note"],
        [":itemId", "itemName", "Item"],
        [":groupId", "groupName", "Group"],
        [":trialId", "trialName", "Trial"],
        [":institutionId", "institutionName", "Institution"],
        [":trialGroupId", "trialGroupName", "Trial Group"],
        [":projectId", "projectName", "Project"],
        [":formId", "formName", "Form"],
    ];
    const parameter = parameterLabels.find(([name]) => path === name);
    if (parameter) {
        return context.params[parameter[0].slice(1)] === "create" ? "Create" : context.labels[parameter[1]] ?? parameter[2];
    }
    const configured = topic ? dynamicTopicLabels[topic] : undefined;
    if (configured) return context.labels[configured[0]] ?? context.labels.lookupName ?? configured[1];
    if (context.labels.lookupName) return context.labels.lookupName;
    if (topic && path.startsWith(":")) return humanizeSegment(topic);
    return humanizeSegment(path);
};

export const defaultRouteTitle = (path?: string): string => {
    if (!path) return "Trial Management";
    const segment = path.split("/").filter(Boolean).at(-1);
    if (!segment || segment.startsWith(":")) return "Details";
    if (segment === "create") return "Create";
    return legacyRouteLabels[segment] ?? humanizeSegment(segment);
};

export const defaultRouteLabel = (match: RouteMatchLike): string => {
    const path = match.route?.path;
    if (path?.startsWith(":")) {
        const key = path.slice(1);
        return match.params[key] ? `#${match.params[key]}` : humanizeSegment(key);
    }
    return defaultRouteTitle(path);
};

const hasUnresolvedPathParameter = (url: string): boolean => /(^|\/)(?::[A-Za-z0-9_]+|undefined|null)(?=\/|$)/.test(url);

const resolveBreadcrumbUrl = (
    value: RouteMetadataValue<string | undefined> | undefined,
    context: RouteMetadataContext,
    match: RouteMatchLike,
): string | undefined => {
    const url = evaluateRouteValue(value, context, match.pathname);
    if (url === undefined || hasUnresolvedPathParameter(url)) {
        // A breadcrumb must never link to a route that still contains a
        // template parameter. A concrete matched pathname is the safest
        // fallback; synthetic tests with a template pathname get no link.
        return hasUnresolvedPathParameter(match.pathname) ? undefined : match.pathname;
    }
    return url;
};

export const hasRouteAccess = (
    access: AppRouteHandle["access"],
    permissions: readonly Dtos.PersonnelTmdPermissions[],
    params: Readonly<Record<string, string | undefined>> = {},
): boolean => {
    // Keep the application's established convention: a not-yet-loaded empty
    // permission collection does not hide the route.
    if (permissions.length === 0 || !access) return true;
    const trialParam = access.trialParam ?? (params.trialId !== undefined ? "trialId" : undefined);
    const institutionParam = access.institutionParam ?? (params.institutionId !== undefined ? "institutionId" : undefined);
    const trialId = trialParam ? Number(params[trialParam]) : undefined;
    const institutionId = institutionParam ? Number(params[institutionParam]) : undefined;
    const permissionList = [...permissions];
    const scoped = access.permission === undefined || hasPermission(permissionList, access.permission, trialId, institutionId);
    const anyMatch = access.anyMatchPermission === undefined || hasPermissionAnyMatch(permissionList, access.anyMatchPermission);
    return scoped && anyMatch;
};

export const resolveRouteMetadata = (
    matches: readonly RouteMatchLike[],
    labels: Readonly<Record<string, string | undefined>> = {},
    permissions: readonly Dtos.PersonnelTmdPermissions[] = [],
): ResolvedRouteMetadata => {
    const params = matches.reduce<Record<string, string | undefined>>((all, match) => ({ ...all, ...match.params }), {});
    const context = { params, labels, permissions } satisfies RouteMetadataContext;
    const breadcrumbs: INavItem[] = [];
    let title = "Trial Management";
    let helpTopicKey: string | null = null;
    let access: AppRouteHandle["access"];

    for (const match of matches) {
        const handle = match.handle ?? match.route?.handle;
        if (!handle) continue;
        if (Object.prototype.hasOwnProperty.call(handle, "title")) {
            title = evaluateRouteValue(handle.title, context, title);
        }
        if (Object.prototype.hasOwnProperty.call(handle, "help")) {
            helpTopicKey = handle.help?.topicKey ?? null;
        }
        if (Object.prototype.hasOwnProperty.call(handle, "access")) access = handle.access;
        if (handle.breadcrumb !== null) {
            const breadcrumb = handle.breadcrumb;
            const label = evaluateRouteValue(breadcrumb?.label, context, defaultRouteLabel(match));
            const include = typeof breadcrumb?.include === "function" ? breadcrumb.include(context) : breadcrumb?.include !== false;
            if (include && label) {
                const item = {
                    title: label,
                    icon: breadcrumb?.icon,
                    url: resolveBreadcrumbUrl(breadcrumb?.url, context, match),
                };
                // Parent/index pairs often describe the same destination.
                // Keep the first declaration so legacy chains contain one
                // item per destination while still allowing distinct labels
                // that intentionally share a URL (Entity > Details).
                const duplicate = breadcrumbs.some(previous => previous.title === item.title && previous.url === item.url);
                if (item.title !== "Trial Management" && !duplicate) breadcrumbs.push(item);
            }
        }
    }

    if (matches.length > 0) {
        const leaf = matches.at(-1)!;
        const leafHandle = leaf.handle ?? leaf.route?.handle;
        if (!leafHandle?.title && leaf.route?.path !== undefined) title = defaultRouteTitle(leaf.route.path) || title;
    }

    return { title, helpTopicKey, breadcrumbs, drawerItems: routeDrawerItems(matches, permissions, labels), access, params: context.params };
};

/** Resolves the contextual drawer from the active route's declared sibling destinations. */
export const routeDrawerItems = (
    matches: readonly RouteMatchLike[],
    permissions: readonly Dtos.PersonnelTmdPermissions[] = [],
    labels: Readonly<Record<string, string | undefined>> = {},
): INavItem[] => {
    const params = matches.reduce<Record<string, string | undefined>>((all, match) => ({ ...all, ...match.params }), {});
    const context = { params, labels, permissions } satisfies RouteMetadataContext;
    // UIMatch does not contain route children.  The hook enriches each match
    // with the corresponding route object from matchRoutes; use that object
    // to recover the active destination's actual sibling scope.
    for (let index = matches.length - 1; index > 0; index -= 1) {
        const active = matches[index];
        if (active.route?.index || !active.route?.path) continue;
        const parentMatch = matches[index - 1];
        const parent = parentMatch.route;
        if (!parent) continue;
        const candidates = (parent.children ?? [])
            .filter((route): route is AppRouteObject & { path: string } => Boolean(route.path && !route.index && route.handle?.navigation?.drawer && !route.handle.navigation.primary))
            .filter(route => !route.path.includes(":") && !route.path.split("/").some(segment => segment === "create"))
            .filter(route => !route.element)
            .filter(route => !route.handle?.access || hasRouteAccess(route.handle.access, permissions, params));
        // The trial's project details route is in the sibling :projectId branch.
        const trialProjectDetails = parent.path === ":trialId" && matches[index - 2]?.route?.path === "trials"
            ? matches[index - 2].route?.children?.find(route => route.path === ":projectId")
                ?.children?.find(route => route.path === "project")
                ?.children?.find(route => route.path === "details")
            : undefined;
        const trialProject = trialProjectDetails && (!trialProjectDetails.handle?.access || hasRouteAccess(trialProjectDetails.handle.access, permissions, params))
            ? [{ title: "Project", url: `${parentMatch.pathname}/project/details`, description: undefined, icon: FontAwesomeIcons.Duotone.CLIPBOARD, order: 11 }]
            : [];
        if (candidates.length === 0 && trialProject.length === 0) continue;
        const items = [...candidates
            .map(route => {
                const navigation = route.handle!.navigation!;
                const title = evaluateRouteValue(navigation.title ?? route.handle?.title, context, defaultRouteTitle(route.path));
                const url = interpolatePath(`${parentMatch.pathname}/${route.path}`, params);
                return {
                    title,
                    url,
                    description: evaluateRouteValue(navigation.description, context, undefined),
                    icon: navigation.icon,
                    order: navigation.order ?? Number.MAX_SAFE_INTEGER,
                };
            }), ...trialProject]
            .sort((a, b) => a.order - b.order)
            .map(({ order: _order, ...item }) => item);
        const backTo = parent.handle?.navigation?.backTo;
        const backUrl = evaluateRouteValue(backTo, context, undefined)
            ?? (parentMatch.route?.path?.startsWith(":") ? matches[index - 2]?.pathname : parentMatch.pathname);
        if (backUrl) {
            items.push({ title: "Back", url: interpolatePath(backUrl, params), description: undefined, icon: FontAwesomeIcons.Duotone.LEFTTOLINE });
        }
        return items;
    }
    return [];
};

const interpolatePath = (path: string, params: Readonly<Record<string, string | undefined>>): string => {
    const resolved = path.replace(/:([A-Za-z0-9_]+)/g, (_match, key: string) => params[key] ?? `:${key}`).replace(/\/+/g, "/");
    return resolved.startsWith("/") ? resolved : `/${resolved}`;
};

/** Adds explicit defaults once at the route boundary, without touching lazy modules. */
export const withRouteMetadata = (routes: AppRouteObject[], depth = 0, parentPath = ""): AppRouteObject[] => routes.map(route => {
    const existing = route.handle ?? {};
    const help = Object.prototype.hasOwnProperty.call(existing, "help") ? existing.help : null;
    const path = route.path;
    const fullPath = path ? `${parentPath}/${path}`.replace(/\/+/g, "/") : parentPath;
    const primaryNavigation = depth === 1 && path ? primaryNavigationDefaults[path] : undefined;
    const topicKey = existing.help?.topicKey;
    const helpRoute = topicKey
        ? helpRouteCatalog.find(candidate => candidate.path === fullPath && candidate.topicKey === topicKey)
            ?? helpRouteCatalog.find(candidate => candidate.topicKey === topicKey)
        : undefined;
    const helpPermission = helpRoute ? helpRoutePermission(helpRoute.id) : undefined;
    const title = existing.title ?? (path === undefined
        ? undefined
        : path.startsWith(":")
            ? ((context: RouteMetadataContext) => dynamicRouteLabel(topicKey, path, context))
            : defaultRouteTitle(path));
    const breadcrumb = existing.breadcrumb === undefined
        ? (title ? { label: title, icon: defaultRouteIcon(path) ?? dynamicTopicIcon(topicKey), include: !route.index } : null)
        : existing.breadcrumb;
    const access = existing.access === undefined && primaryNavigation?.access !== undefined
        ? primaryNavigation.access
        : existing.access === undefined && helpPermission !== undefined && helpPermission !== null
        ? {
            permission: helpPermission,
            trialParam: fullPath.includes(":trialId") ? "trialId" : undefined,
            institutionParam: fullPath.includes(":institutionId")
                ? "institutionId"
                : fullPath.includes(":trialInstitutionId")
                    ? "trialInstitutionId"
                    : undefined,
        }
        : existing.access;
    const explicitNavigation = fullPath ? settingsNavigationDefaults[fullPath] ?? legacyDrawerNavigationDefaults[fullPath] : undefined;
    const navigation = existing.navigation === undefined && primaryNavigation
        ? { ...primaryNavigation.navigation, drawer: true }
        : explicitNavigation
            ? { ...explicitNavigation, ...existing.navigation }
            : existing.navigation;
    const children = route.children ? withRouteMetadata(route.children, depth + 1, fullPath) : undefined;
    const hasDrawerChildren = children?.some(child => child.handle?.navigation?.drawer && !child.handle.navigation.primary) === true;
    const menuBackTo = hasDrawerChildren
        ? path?.startsWith(":")
            ? parentPath
            : fullPath
        : undefined;
    const resolvedNavigation = navigation
        ? menuBackTo && navigation.backTo === undefined
            ? { ...navigation, backTo: menuBackTo }
            : navigation
        : menuBackTo
            ? { backTo: menuBackTo }
            : undefined;
    const resolvedAccess = access === undefined ? children?.find(child => child.index)?.handle?.access : access;
    return {
        ...route,
        handle: { ...existing, title, help, breadcrumb, access: resolvedAccess ?? null, navigation: resolvedNavigation },
        children,
    } as AppRouteObject;
});

export const routeNavigationItems = (
    routes: readonly AppRouteObject[],
    permissions: readonly Dtos.PersonnelTmdPermissions[] = [],
): INavItem[] => routes
    .filter(route => route.handle?.navigation?.primary && route.path)
    // Unlike the route boundary, the header must not advertise permissioned
    // destinations while authentication is still loading. Dashboard and
    // search have no access requirement and remain available in that state.
    .filter(route => !route.handle?.access || (permissions.length > 0 && hasRouteAccess(route.handle.access, permissions)))
    .map(route => {
        const navigation = route.handle!.navigation!;
        const title = evaluateRouteValue(navigation.title ?? route.handle?.title, { params: {}, labels: {}, permissions }, defaultRouteTitle(route.path));
        return {
            title,
            url: route.path!.startsWith("/") ? route.path : `/${route.path}`,
            description: evaluateRouteValue(navigation.description, { params: {}, labels: {}, permissions }, undefined),
            icon: navigation.icon,
        };
    })
    .sort((a, b) => (routes.find(route => route.path === a.url?.slice(1))?.handle?.navigation?.order ?? 0) - (routes.find(route => route.path === b.url?.slice(1))?.handle?.navigation?.order ?? 0));
